import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Receipt,
  ShoppingCart,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Loader2
} from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';

const PriceHistoryTable = ({
  data,
  pageSize = 10,
  sortable = true,
  showActions = false,
  showProductIdColumn = false,
  onApplyPrice,
  // Infinite scroll props
  infiniteScroll = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  totalRecords = 0
}) => {
  // Set default values for props
  const tableData = useMemo(() => (data && Array.isArray(data) ? data : []), [data]);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const scrollContainerRef = useRef(null);

  // Table state
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);

  // Infinite scroll detection
  const handleScroll = useCallback(() => {
    if (!infiniteScroll || !hasMore || loadingMore || !onLoadMore) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Trigger load more when user scrolls to within 100px of bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      onLoadMore();
    }
  }, [infiniteScroll, hasMore, loadingMore, onLoadMore]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !infiniteScroll) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [infiniteScroll, handleScroll]);

  // Filter state
  const [filters] = useState({
    client: 'all',
    sourceType: 'all',
    priceRange: { min: '', max: '' }
  });


  // Sort and filter data
  const processedData = useMemo(() => {
    // Ensure we have valid data
    if (!tableData || tableData.length === 0) return [];

    // Apply filters
    let filteredData = tableData.filter(item => {
      // Ensure item has required properties
      if (!item) return false;

      const clientMatch = !filters.client || filters.client === 'all' ||
        (item.client_name && String(item.client_name).toLowerCase().includes(filters.client.toLowerCase())) ||
        (item.customer_name && String(item.customer_name).toLowerCase().includes(filters.client.toLowerCase())) ||
        (item.client && String(item.client).toLowerCase().includes(filters.client.toLowerCase()));

      const sourceMatch = !filters.sourceType || filters.sourceType === 'all' ||
        (item.source_type === filters.sourceType) ||
        (item.type === filters.sourceType);

      const itemPrice = parseFloat(item.price || item.unit_price || 0);
      const priceMatch = (!filters.priceRange.min || itemPrice >= parseFloat(filters.priceRange.min || 0)) &&
                        (!filters.priceRange.max || itemPrice <= parseFloat(filters.priceRange.max || Infinity));

      return clientMatch && sourceMatch && priceMatch;
    });

    // Apply sorting
    if (sortable) {
      filteredData.sort((a, b) => {
        // Ensure items have the properties we're sorting by
        if (!a || !b) return 0;

        let aVal = a[orderBy];
        let bVal = b[orderBy];

        // Handle different data types
        if (orderBy === 'date') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        } else if (orderBy === 'price' || orderBy === 'quantity') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (order === 'desc') {
          return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
        } else {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        }
      });
    }

    return filteredData;
  }, [tableData, filters, orderBy, order, sortable]);

  // Paginated data - skip pagination when using infinite scroll
  const paginatedData = useMemo(() => {
    // Ensure we have valid data and page values
    if (!processedData || !Array.isArray(processedData)) return [];

    // When using infinite scroll, show all loaded data (already paginated from server)
    if (infiniteScroll) {
      return processedData;
    }

    // Client-side pagination
    const startIndex = Math.max(0, (page - 1) * pageSize);
    const endIndex = startIndex + pageSize;
    return processedData.slice(startIndex, endIndex);
  }, [processedData, page, pageSize, infiniteScroll]);

  const totalPages = infiniteScroll ? 1 : Math.ceil(processedData.length / pageSize);

  // Handle sorting
  const handleSort = (property) => {
    if (!sortable) return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort header component
  const SortableHeader = ({ property, children, align = 'left' }) => {
    if (!sortable) {
      return <th className={`p-3 font-medium text-${align}`}>{children}</th>;
    }

    return (
      <th
        className={`p-3 font-medium text-${align} cursor-pointer hover:bg-muted select-none`}
        onClick={() => handleSort(property)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
          {children}
          {orderBy === property ? (
            order === 'desc' ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )
          ) : (
            <ChevronsUpDown className="h-4 w-4 opacity-30" />
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="pb-1">
      

      {/* Table */}
      <Card className="border overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="overflow-auto max-h-[70vh] min-h-[400px]"
        >
          <table className={`w-full text-sm ${isMobile ? 'text-xs' : ''}`}>
            <thead className="bg-muted sticky top-0">
              <tr className="border-b-2">
                <SortableHeader property="source_type">类型</SortableHeader>
                {showProductIdColumn && !isMobile && <SortableHeader property="product_id">产品编号</SortableHeader>}
                {!isMobile && <SortableHeader property="product_name">产品名称</SortableHeader>}
                {!isMobile && <SortableHeader property="specification">规格</SortableHeader>}
                <SortableHeader property="client_name">客户</SortableHeader>
                <SortableHeader property="date" align="right">日期</SortableHeader>
                <SortableHeader property="tax_exclusive_price" align="right">不含税价格</SortableHeader>
                <SortableHeader property="tax_inclusive_price" align="right">含税价格</SortableHeader>
                {!isMobile && <th className="p-3 font-medium text-center">税率</th>}
                {!isMobile && <SortableHeader property="quantity" align="center">数量</SortableHeader>}
                {showActions && <th className="p-3 font-medium text-center">操作</th>}
              </tr>
            </thead>

            <tbody>
              {paginatedData.map((row, index) => (
                <tr
                  key={`${row.reference_id || row.id || index}-${index}`}
                  className="border-b hover:bg-muted"
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {(row.source_type || row.type) === 'quotation' ? (
                        <Receipt className="h-4 w-4 text-primary" />
                      ) : (
                        <ShoppingCart className="h-4 w-4 text-success" />
                      )}
                      <Badge
                        variant="outline"
                        className={(row.source_type || row.type) === 'quotation' ? 'border-blue-500 text-primary' : 'border-green-500 text-success'}
                      >
                        {(row.source_type || row.type) === 'quotation' ? '报价单' : '销售订单'}
                      </Badge>
                    </div>
                  </td>

                  {showProductIdColumn && !isMobile && (
                    <td className="p-3">
                      <span className="font-mono text-sm">{row.product_id || '-'}</span>
                    </td>
                  )}

                  {!isMobile && (
                    <td className="p-3">
                      <span className="text-sm">{row.product_name || row.name || '-'}</span>
                    </td>
                  )}

                  {!isMobile && (
                    <td className="p-3">
                      <span className="text-sm text-muted-foreground">{row.specification || row.spec || '-'}</span>
                    </td>
                  )}

                  <td className="p-3">
                    <span className="font-medium">
                      {row.client_name || row.customer_name || row.client || '未知客户'}
                    </span>
                    {(row.price_column || row.description) && (
                      <span className="block text-xs text-muted-foreground">
                        {row.price_column || row.description}
                      </span>
                    )}
                  </td>

                  <td className="p-3 text-right">
                    <span>
                      {row.date ? (() => {
                        // Try to parse the date in various formats
                        let dateObj;
                        if (row.date instanceof Date) {
                          dateObj = row.date;
                        } else if (typeof row.date === 'string') {
                          // Try parsing as ISO string first
                          dateObj = new Date(row.date);
                          // If invalid, try other formats
                          if (isNaN(dateObj.getTime())) {
                            // Add more date parsing logic here if needed
                            dateObj = new Date();
                          }
                        } else if (typeof row.date === 'number') {
                          // Assume it's a timestamp
                          dateObj = new Date(row.date);
                        } else {
                          dateObj = new Date();
                        }

                        return dateObj.toLocaleDateString('zh-CN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                      })() : '无日期'}
                    </span>
                  </td>

                  <td className="p-3 text-right">
                    <span className="font-medium">
                      ¥{parseFloat(row.tax_exclusive_price || row.price || row.unit_price || 0).toFixed(2)}
                    </span>
                  </td>

                  <td className="p-3 text-right">
                    <span className="font-bold text-primary">
                      ¥{parseFloat(row.tax_inclusive_price || (row.price || row.unit_price || 0) * 1.13).toFixed(2)}
                    </span>
                  </td>

                  {!isMobile && (
                    <td className="p-3 text-center">
                      <Badge
                        variant="outline"
                        className={row.pricing_mode === 'tax_inclusive' ? 'border-yellow-500 text-warning' : ''}
                      >
                        {row.formatted_tax_rate || `${(parseFloat(row.tax_rate || 0.13) * 100).toFixed(1)}%`}
                      </Badge>
                    </td>
                  )}

                  {!isMobile && (
                    <td className="p-3 text-center">
                      <span>{row.quantity || row.qty || 1}</span>
                    </td>
                  )}

                  {showActions && (
                    <td className="p-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => {
                          if (!onApplyPrice) return;
                          const selectedPrice = parseFloat(
                            row.tax_exclusive_price ?? row.price ?? row.unit_price ?? 0
                          ) || 0;
                          const payload = {
                            // Numeric convenience fields for consumers
                            price: selectedPrice,
                            unit_price: selectedPrice,
                            // Preserve detailed fields
                            base_price: row.base_price ?? row.price ?? row.unit_price ?? selectedPrice,
                            pricing_mode: row.pricing_mode || 'tax_exclusive',
                            tax_rate: row.tax_rate || 0.13,
                            tax_exclusive_price: row.tax_exclusive_price ?? selectedPrice,
                            tax_inclusive_price: row.tax_inclusive_price ?? (selectedPrice * 1.13),
                            tax_amount: row.tax_amount ?? (selectedPrice * (row.tax_rate || 0.13)),
                            formatted_tax_rate: row.formatted_tax_rate,
                            source: {
                              type: row.source_type || row.type,
                              reference_id: row.reference_id,
                              client_name: row.client_name,
                              date: row.date
                            }
                          };
                          onApplyPrice(payload);
                        }}
                      >
                        应用
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {/* Loading more indicator for infinite scroll */}
              {infiniteScroll && loadingMore && (
                <tr>
                  <td
                    colSpan={!isMobile ? (9 + (showProductIdColumn ? 1 : 0) + (showActions ? 1 : 0)) : 5}
                    className="p-4 text-center"
                  >
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>加载更多...</span>
                    </div>
                  </td>
                </tr>
              )}
              {/* End of data indicator for infinite scroll */}
              {infiniteScroll && !hasMore && paginatedData.length > 0 && (
                <tr>
                  <td
                    colSpan={!isMobile ? (9 + (showProductIdColumn ? 1 : 0) + (showActions ? 1 : 0)) : 5}
                    className="p-3 text-center text-xs text-muted-foreground border-t"
                  >
                    已加载全部 {totalRecords || processedData.length} 条记录
                  </td>
                </tr>
              )}
              {/* Add an empty row for spacing if there are records (non-infinite scroll) */}
              {!infiniteScroll && paginatedData.length > 0 && (
                <tr className="h-4">
                  <td colSpan={!isMobile ? (9 + (showProductIdColumn ? 1 : 0) + (showActions ? 1 : 0)) : 5} className="p-0 border-none" />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination and Record Count */}
      <div className={`flex mt-4 ${!infiniteScroll && totalPages > 1 ? 'justify-between' : 'justify-start'} items-center`}>
        <span className="text-sm text-muted-foreground">
          {infiniteScroll ? (
            <>
              已加载 {processedData.length} / {totalRecords || processedData.length} 条记录
              {hasMore && ' (滚动加载更多)'}
            </>
          ) : (
            <>
              共 {processedData.length} 条记录
              {totalPages > 1 && ` (第 ${page} 页，共 ${totalPages} 页)`}
              {totalPages <= 1 && processedData.length > 10 && ' (可滚动查看)'}
              {totalPages > 1 && ` | 显示第 ${Math.max(1, (page - 1) * pageSize + 1)}-${Math.min(processedData.length, page * pageSize)} 条`}
            </>
          )}
        </span>
        {!infiniteScroll && totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              上一页
            </Button>
            <span className="px-2 text-sm">{page} / {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              下一页
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceHistoryTable;
